import { Component, signal, OnInit, inject, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { trigger, style, animate, transition } from '@angular/animations';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import HeaderComponent from '../header/header.component';
import FooterComponent from '../footer/footer/footer.component';
import { Icon } from '@millionshow/client-libs';
import { NAV_ITEMS } from '../../const/nav-items.const';
import FloatingButtonsComponent from '../../components/floating-buttons/floating-buttons.component';
import { ScreenSizeStore } from '../../stores/screen-size.store';
import { SettingService } from '@millionshow/api-client';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-client-layout',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    HeaderComponent,
    FooterComponent,
    Icon,
    FloatingButtonsComponent,
  ],
  templateUrl: './client-layout.component.html',
  animations: [
    trigger('slideInOut', [
      transition(':enter', [
        style({ transform: 'translateX(100%)', opacity: 0 }),
        animate(
          '300ms ease-out',
          style({ transform: 'translateX(0)', opacity: 1 })
        ),
      ]),
      transition(':leave', [
        animate(
          '300ms ease-in',
          style({ transform: 'translateX(100%)', opacity: 0 })
        ),
      ]),
    ]),
    trigger('slideDown', [
      transition(':enter', [
        style({ transform: 'translateY(-100%)', opacity: 0 }),
        animate(
          '400ms ease-out',
          style({ transform: 'translateY(0)', opacity: 1 })
        ),
      ]),
      transition(':leave', [
        animate(
          '300ms ease-in',
          style({ transform: 'translateY(-100%)', opacity: 0 })
        ),
      ]),
    ]),
  ],
})
export default class ClientLayoutComponent implements OnInit {
  private readonly router = inject<Router>(Router);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly settingService = inject(SettingService);

  private get isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  menuOpen = signal<boolean>(false);
  logoData = toSignal(
    this.settingService.settingControllerFindByKey({
      key: 'goodspace_logo_url',
    })
  );
  isHomePage = signal<boolean>(false);
  navItems = NAV_ITEMS;

  relatedSites = [
    { label: '밀리언쇼', url: 'https://millionshow.kr/' },
    { label: '건축박람회', url: 'https://www.allhouse.or.kr/' },
    { label: '명절선물박람회', url: 'http://www.giftfair.co.kr/' },
    { label: '농기자재전시회', url: 'https://www.agritex.kr/' },
    { label: '창업박람회', url: 'https://www.startfair.net/' },
  ];

  private readonly screenSizeStore = inject(ScreenSizeStore);
  isMobileSize = this.screenSizeStore.isMobileSize;
  isFloatingHiddenPage = signal<boolean>(false);
  isStickyHiddenPage = signal<boolean>(false);

  ngOnInit(): void {
    this.updatePageStatus();

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        this.updatePageStatus();
      });
  }

  updatePageStatus(): void {
    const url = this.router.url;
    this.isHomePage.set(url === '/' || url === '');

    // 모바일에서 플로팅 버튼/스티키 버튼을 가릴 페이지
    const isPolicy = url.includes('/policy');
    const isEventDetail =
      url.startsWith('/event/') && url.split('/').length > 2;
    const isInquiry = url.includes('/inquiry');

    this.isFloatingHiddenPage.set(isPolicy || isEventDetail);
    this.isStickyHiddenPage.set(isPolicy || isEventDetail || isInquiry);
  }

  toggleMenu(): void {
    this.menuOpen.set(!this.menuOpen());
    if (this.isBrowser) {
      document.body.style.overflow = this.menuOpen() ? 'hidden' : '';
    }
  }
}
